import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CircleDollarSign,
  Clock4,
  FileText,
  Receipt as ReceiptIcon,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, formatNumber } from "@/lib/format";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";

interface RecentReceipt {
  id: number;
  receipt_number: string;
  customer_name: string;
  amount_paid: number;
  payment_method: string;
  payment_date: string | null;
  created_at: string | null;
}

interface RecentInvoice {
  id: number;
  invoice_number: string;
  customer_name: string;
  total_amount: number;
  invoice_date: string | null;
  created_at: string | null;
}

interface SummaryResponse {
  totalRevenue: number;
  outstanding: number;
  receiptsCount: number;
  invoicesCount: number;
  recentReceipts: RecentReceipt[];
  recentInvoices: RecentInvoice[];
}

export default function Finance() {
  const { canAccess } = useAuth();
  const showCustomerLedger = canAccess("customer_ledger");
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<SummaryResponse>("/finance/summary");
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled)
          toast.error((err as Error).message || "Failed to load Finance summary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = [
    {
      label: "Total Revenue",
      value: summary ? formatNumber(summary.totalRevenue) : "…",
      hint: "Cash-in across all receipts",
      icon: CircleDollarSign,
      tone: "from-emerald-500 to-teal-400",
    },
    {
      label: "Outstanding",
      value: summary ? formatNumber(summary.outstanding) : "…",
      hint: "Invoiced amount yet to be received",
      icon: Clock4,
      tone: "from-amber-500 to-orange-400",
    },
    {
      label: "Receipts (this month)",
      value: summary ? String(summary.receiptsCount) : "…",
      hint: "Count of receipts created this month",
      icon: ReceiptIcon,
      tone: "from-violet-500 to-fuchsia-400",
    },
    {
      label: "Invoices (this month)",
      value: summary ? String(summary.invoicesCount) : "…",
      hint: "Count of sales invoices this month",
      icon: FileText,
      tone: "from-blue-500 to-cyan-400",
    },
  ];

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Finance</h1>
        <p className="text-muted-foreground">
          A read-only overview of cash-in, outstanding receivables and recent
          financial activity.
        </p>
      </div>

      {showCustomerLedger ? (
        <Card className="shadow-card border-cyan-200/60 dark:border-cyan-900/40">
          <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-card shrink-0">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="font-semibold">Customer Ledger</div>
                <div className="text-sm text-muted-foreground">
                  Accounts receivable — customer balances, aging and statements
                </div>
              </div>
            </div>
            <Button asChild variant="outline">
              <Link to="/finance/customer-ledger">Open Customer Ledger</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Card key={card.label} className="shadow-card overflow-hidden">
            <CardContent className="p-5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-muted-foreground">{card.label}</div>
                <div className="text-3xl font-bold mt-1 truncate">
                  {card.value}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {card.hint}
                </div>
              </div>
              <div
                className={`h-12 w-12 rounded-xl bg-gradient-to-br ${card.tone} flex items-center justify-center shadow-card shrink-0`}
              >
                <card.icon className="h-6 w-6 text-white" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg">Recent Sales Invoices</CardTitle>
            <CardDescription>Latest 10 sales invoices.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground py-10"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : !summary?.recentInvoices?.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground py-10"
                    >
                      No sales invoices yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  summary.recentInvoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-mono">
                        {inv.invoice_number}
                      </TableCell>
                      <TableCell className="font-medium">
                        {inv.customer_name || "—"}
                      </TableCell>
                      <TableCell>
                        {inv.invoice_date ? formatDate(inv.invoice_date) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(inv.total_amount)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg">Recent Receipts</CardTitle>
            <CardDescription>Latest 10 receipts.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-10"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : !summary?.recentReceipts?.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-10"
                    >
                      No receipts yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  summary.recentReceipts.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono">
                        {r.receipt_number}
                      </TableCell>
                      <TableCell className="font-medium">
                        {r.customer_name || "—"}
                      </TableCell>
                      <TableCell className="capitalize">
                        {r.payment_method || "Cash"}
                      </TableCell>
                      <TableCell>
                        {r.payment_date ? formatDate(r.payment_date) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(r.amount_paid)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
